import { SITE_LOCALES, localeFromPath, pathForLocale, type SiteLocale } from "./content/index.js";
import homePt from "./v2/content.js";
import type { V2Content } from "./v2/content.js";
import homeEn from "./v2/content-en.js";
import homeEs from "./v2/content-es.js";
import type { FlowVariant } from "./v2/flow.js";
import planner, { DEMO_PATH, type PlannerV2Content } from "./v2/planner-content.js";

export interface HomePage {
  readonly id: "home";
  readonly path: string;
  readonly locale: SiteLocale;
  readonly content: V2Content;
  readonly alternates: true;
}

export interface PlannerPage {
  readonly id: "cerimonialistas";
  readonly path: "/cerimonialistas/";
  readonly locale: "pt-BR";
  readonly content: PlannerV2Content;
  readonly alternates: false;
}

export interface DemoPageCopy {
  readonly meta: { readonly title: string; readonly description: string };
}

export interface DemoPage {
  readonly id: "demo";
  readonly path: typeof DEMO_PATH;
  readonly locale: "pt-BR";
  readonly content: DemoPageCopy;
  readonly alternates: false;
}

export interface ScrollTestPage {
  readonly id: `rolagem-${FlowVariant}`;
  readonly path: `/teste/rolagem-${FlowVariant}/`;
  readonly locale: "pt-BR";
  readonly content: V2Content;
  readonly alternates: false;
  readonly variant: FlowVariant;
}

export type SitePage = HomePage | PlannerPage | DemoPage | ScrollTestPage;

const HOME_CONTENT: Record<SiteLocale, V2Content> = { "pt-BR": homePt, en: homeEn, es: homeEs };

export const HOME_FLOW_VARIANT: FlowVariant = "a";

export function homePage(locale: SiteLocale): HomePage {
  return {
    id: "home",
    path: pathForLocale(locale),
    locale,
    content: HOME_CONTENT[locale],
    alternates: true,
  };
}

const PLANNER_PAGE: PlannerPage = {
  id: "cerimonialistas",
  path: "/cerimonialistas/",
  locale: "pt-BR",
  content: planner,
  alternates: false,
};

const DEMO_PAGE: DemoPage = {
  id: "demo",
  path: DEMO_PATH,
  locale: "pt-BR",
  content: {
    meta: {
      title: "Demonstração · Kuori para cerimonialistas",
      description:
        "Monte um evento de exemplo com a marca do seu cerimonial e veja convite, câmera, telão, moderação e álbum funcionando, direto no navegador.",
    },
  },
  alternates: false,
};

const SCROLL_VARIANTS: readonly FlowVariant[] = ["a", "b", "c", "d", "snap"];

export const SCROLL_TEST_PAGES: readonly ScrollTestPage[] = SCROLL_VARIANTS.map((variant) => ({
  id: `rolagem-${variant}`,
  path: `/teste/rolagem-${variant}/`,
  locale: "pt-BR",
  content: homePt,
  alternates: false,
  variant,
}));

export const SITE_PAGES: readonly SitePage[] = [
  ...SITE_LOCALES.map(homePage),
  PLANNER_PAGE,
  DEMO_PAGE,
  ...SCROLL_TEST_PAGES,
];

function trimmed(path: string): string {
  return path.replace(/\/+$/, "") || "/";
}

export function pageForPath(pathname: string): SitePage {
  const wanted = trimmed(pathname);
  const match = SITE_PAGES.find((page) => page.id !== "home" && trimmed(page.path) === wanted);
  return match ?? homePage(localeFromPath(pathname));
}

export function pageFor(id: string | undefined, pathname: string): SitePage {
  const byId = id && id !== "home" ? SITE_PAGES.find((page) => page.id === id) : undefined;
  return byId ?? pageForPath(pathname);
}
